import type { ChangeEvent, DragEvent, InputHTMLAttributes } from "react";

import { useEffect, useRef, useState } from "react";
import { useController } from "react-hook-form";
import { Upload } from "lucide-react";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import { useAzureVideoDownload } from "src/components/AssetUploaders/useAzureVideoDownload";
import VideoUploadProgress from "src/components/AssetUploaders/VideoUploadProgress";
import VideoProcessingProgress from "src/components/AssetUploaders/VideoProcessingProgress";
import { useVideoEncodingProgress } from "src/components/AssetUploaders/useVideoEncodingProgress";

export interface FileUploadStepBaseProps {
	name: string;
	accept: InputHTMLAttributes<HTMLInputElement>["accept"];
	label?: string;
	multiple?: InputHTMLAttributes<HTMLInputElement>["multiple"];
	helperText?: string;
	btnText?: string;
	btnColor?: string;
	btnVariant?: string;
}

export interface VideoUploadProps extends FileUploadStepBaseProps {
	fileType: "video";
	imageProfile?: undefined;
}

export interface ImageUploadProps extends FileUploadStepBaseProps {
	fileType: "image";
	imageProfile: ImageProfile;
}

export type FileUploadStepProps = VideoUploadProps | ImageUploadProps;

const FileUploadStep = ({
	name,
	label,
	accept,
	multiple = false,
	imageProfile,
	fileType
}: FileUploadStepProps) => {
	const { field } = useController({ name });
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	// Local blob URL for the just-selected file, so we can show a
	// playable preview during chunked upload AND during Bitmovin
	// encoding — same pattern the announcement editor uses, so all
	// admin video upload sites feel identical: pick file → see it
	// playing immediately with progress overlaid → stays playing
	// while encoding finishes in the background.
	const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

	const {
		upload: uploadFileToAzure,
		uploading: isUploading,
		progress,
		videoUploadState,
		abortVideoUpload
	} = useAzureFileUpload();

	// Revoke local blob URLs to avoid memory leaks. Cleanup runs both
	// on unmount and whenever the URL is replaced (new file picked).
	useEffect(() => {
		const url = localPreviewUrl;
		if (!url) return;
		return () => URL.revokeObjectURL(url);
	}, [localPreviewUrl]);

	const videoUuid = fileType === "video" && field.value ? field.value : null;
	const {
		url: videoUrl,
		thumbnail: videoThumbnail,
		processingStatus
	} = useAzureVideoDownload("video", "", videoUuid);
	const encodingState = useVideoEncodingProgress(
		videoUuid,
		Boolean(videoUuid) && !videoUrl && processingStatus !== "ReadyForPlayback"
	);

	const handleClick = () => {
		inputRef.current?.click();
	};

	const validateFile = (file: File) => {
		if (!accept) return true;

		const allowedExtensions = accept
			.split(",")
			.map((type) => type.trim().toUpperCase().replace("IMAGE/", ""));

		const isValid = allowedExtensions.includes(
			file.type.toUpperCase().replace("IMAGE/", "")
		);

		if (!isValid) {
			alert(`Only ${allowedExtensions.join(" / ")} file types can be selected`);
		}

		return isValid;
	};

	const processUpload = async (file: File) => {
		if (!validateFile(file)) return;

		try {
			if (fileType === "image") {
				const uuid = await uploadFileToAzure(
					file,
					"images",
					undefined,
					imageProfile
				);
				if (uuid) field.onChange(uuid);
			}

			if (fileType === "video") {
				// Capture a local blob URL so the user sees their video
				// playing immediately (matches announcement editor UX).
				// The URL is revoked when state replaces it via the effect
				// above, or when this component unmounts.
				setLocalPreviewUrl(URL.createObjectURL(file));
				const videoUuid = await uploadFileToAzure(file, "video");
				if (videoUuid) {
					field.onChange(videoUuid);
				} else {
					// Upload failed or was aborted — clear the local
					// preview so the UI doesn't keep showing a "selected"
					// video that has no backing asset, and reset the
					// underlying form field for parity.
					setLocalPreviewUrl(null);
					if (inputRef.current) inputRef.current.value = "";
				}
			}
		} catch (error) {
			console.error("Failed to upload file to azure services", error);
			if (fileType === "video") {
				setLocalPreviewUrl(null);
				if (inputRef.current) inputRef.current.value = "";
			}
		}
	};

	const handleCancelUpload = () => {
		abortVideoUpload();
		// Mirror the abort on the local-preview side so the UI
		// returns to the empty dropzone, not a stale "selected"
		// preview backed by no real asset.
		setLocalPreviewUrl(null);
		if (inputRef.current) inputRef.current.value = "";
	};

	const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const fileList = event.target.files;
		if (!fileList) return;
		await processUpload(fileList[0]);
	};

	const handleDragOver = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = async (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);

		const file = e.dataTransfer.files[0];
		if (!file) return;
		await processUpload(file);
	};

	const handleRemoveFile = () => {
		field.onChange("");
		setLocalPreviewUrl(null);
		if (inputRef.current) inputRef.current.value = "";
	};

	const hasFile = !!field.value;
	// Show the video frame whenever we have something to render in it:
	// either the persisted asset (post-encoding) or the local blob
	// (during upload + encoding). Identical UX to the announcement
	// editor — user always sees their picked video play immediately.
	const showVideoFrame =
		fileType === "video" && (hasFile || isUploading || !!localPreviewUrl);
	// While a fresh upload is in flight, prefer the local blob —
	// otherwise the user clicking "Change video" on a saved entity
	// would keep seeing the OLD streaming URL until the new upload
	// finishes (because `field.value` still points at the previous
	// asset). After the new upload completes and a new streaming URL
	// resolves, prefer the streaming URL so we get HLS playback.
	const videoFrameSrc =
		localPreviewUrl && (isUploading || !videoUrl)
			? localPreviewUrl
			: (videoUrl ?? localPreviewUrl ?? undefined);

	return (
		<div className="max-w-[480px]">
			{label ? (
				<label className="block text-sm font-medium mb-1">{label}</label>
			) : (
				<span className="sr-only">Upload a file</span>
			)}

			<input
				ref={inputRef}
				type="file"
				value=""
				onChange={handleChange}
				accept={accept}
				multiple={multiple}
				style={{ display: "none" }}
			/>

			{showVideoFrame ? (
				<>
					<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle relative">
						{videoFrameSrc ? (
							<video
								src={videoFrameSrc}
								poster={videoThumbnail || undefined}
								controls
								className="w-full object-contain"
								style={{ aspectRatio: "16/9" }}
								preload="metadata"
							/>
						) : (
							<div
								className="w-full bg-foreground"
								style={{ aspectRatio: "16/9" }}
							/>
						)}
						{/* Overlay phases — same dark overlay variant the
						    announcement editor uses, so the visual is
						    identical across every admin video upload site. */}
						<VideoUploadProgress
							state={isUploading ? (videoUploadState ?? null) : null}
							onCancel={handleCancelUpload}
							variant="overlay"
						/>
						{!isUploading && !videoUrl && processingStatus === "Processing" && (
							<VideoProcessingProgress
								state={encodingState}
								variant="overlay"
							/>
						)}
					</div>
					{!isUploading && (
						<div className="flex items-center gap-2">
							<button
								type="button"
								className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
								onClick={handleClick}
							>
								Change video
							</button>
							<button
								type="button"
								className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
								onClick={handleRemoveFile}
							>
								Remove
							</button>
						</div>
					)}
				</>
			) : hasFile && !isUploading && fileType === "image" ? (
				<>
					<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
						<AzureImage
							uuid={field.value}
							imageProfile={imageProfile}
							style={{
								objectFit: "contain",
								width: "100%",
								height: "auto"
							}}
							alt="Uploaded image preview"
						/>
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
							onClick={handleClick}
						>
							Change image
						</button>
						<button
							type="button"
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
							onClick={handleRemoveFile}
						>
							Remove
						</button>
					</div>
				</>
			) : (
				<div
					role="button"
					tabIndex={0}
					onClick={() => !isUploading && handleClick()}
					onKeyDown={(e) => e.key === "Enter" && !isUploading && handleClick()}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					className={[
						"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
						isDragging
							? "border-primary bg-primary-lighter"
							: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50",
						isUploading ? "pointer-events-none opacity-60" : ""
					].join(" ")}
				>
					{isUploading ? (
						// Image-only path: video uploads always render
						// inside the video frame above (with overlay
						// progress), so they never fall through to the
						// dropzone while uploading.
						<>
							<div className="w-full max-w-[200px] h-2 rounded-full bg-muted overflow-hidden">
								<div
									className="h-full bg-primary rounded-full transition-all duration-300"
									style={{ width: `${Math.round(progress * 100)}%` }}
								/>
							</div>
							<span className="text-sm text-muted-foreground">
								Uploading...
							</span>
						</>
					) : (
						<>
							<Upload className="h-6 w-6 text-muted-foreground" />
							<div>
								<p className="text-sm font-medium text-foreground">
									Drag {fileType} here or click to browse
								</p>
								<p className="text-xs text-muted-foreground mt-1">
									{fileType === "image" ? "JPG or PNG" : "MP4"}
								</p>
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default FileUploadStep;
